import { RepairNextAction } from "@/components/service/RepairNextAction";
import DashboardLayout from "@/components/DashboardLayout";
import { CustomerNoticePanel } from "@/components/CustomerNoticePanel";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/_core/hooks/useAuth";
import { trpc } from "@/lib/trpc";
import { JOB_STATUS_LABELS, JOB_STATUS_ORDER } from "../../../shared/serviceConstants";
import {
  CLARITY_REPAIR_ORDER_WORKFLOW_STEPS,
  genericStatusUpdateOptions,
} from "../../../shared/repairProposalWorkflow";
import { canEnterSuggestedInternalRepairCost } from "../../../shared/roWorkTimer";
import {
  canShowSuggestedRepairCost,
  recordedWorkLabel,
} from "../../../shared/roDetailPresentation";
import {
  RO_FOCUSED_STAGES,
  buildRoFocusedWorkflow,
  roFocusedStageId,
  type RoFocusedStageId,
} from "../../../shared/roFocusedWorkflow";
import { cn } from "@/lib/utils";
import { RoWorkTimerCard } from "@/components/roWorkTimer/RoWorkTimerCard";
import { toast } from "sonner";
import {
  AlertTriangle,
  Bell,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  Clock,
  Copy,
  Info,
  MoreVertical,
  Download,
  FileText,
  Pencil,
  Save,
  Trash2,
  Upload,
  User,
  Wrench,
  CheckCircle2,
  XCircle,
  Send,
  RefreshCw,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { formatDate, formatDateTime } from "@/lib/dateUtils";
import { Link, useLocation, useParams } from "wouter";
import { useHistoryState } from "wouter/use-browser-location";
import { Sparkles } from "lucide-react";
import { repairProposalCustomerSendState } from "@/lib/repairProposalCustomerSend";
import { roLiveWorkPresentation } from "@shared/roRunningPresentation";
import { RoActivityTimeline } from "@/components/service/RoActivityTimeline";
import { RoLiveWorkMark, RoWorkRecordedState } from "@/components/service/RoLiveWorkMark";

// ─── Department colour config ─────────────────────────────────────────────────
// Each section has a left bar colour and an optional triangle pointer.
// The triangle (▶) appears on the section that owns the current status.

type DeptColour = {
  bar: string;          // Tailwind bg class for the 4px left bar
  barHex: string;       // Hex for the SVG triangle
  label: string;        // Department label shown above the bar
  labelText: string;    // Tailwind text class
};

const DEPT_COLOURS: Record<string, DeptColour> = {
  intake: {
    bar: "bg-slate-300",
    barHex: "#94a3b8",
    label: "INTAKE",
    labelText: "text-slate-500",
  },
  service: {
    bar: "bg-amber-400",
    barHex: "#fbbf24",
    label: "SERVICE TEAM",
    labelText: "text-amber-600",
  },
  finance: {
    bar: "bg-blue-400",
    barHex: "#60a5fa",
    label: "FINANCE",
    labelText: "text-blue-600",
  },
  pic: {
    bar: "bg-green-400",
    barHex: "#4ade80",
    label: "PIC / DELIVERY",
    labelText: "text-green-600",
  },
};

// Which card does each status point at?
const STATUS_CARD: Record<string, string> = {
  received: "customer",
  diagnosis: "diagnosis",
  pending_finance: "diagnosis",
  awaiting_commercial_review: "diagnosis",
  ready_to_send_to_customer: "diagnosis",
  awaiting_customer_confirmation: "diagnosis",
  customer_declined: "diagnosis",
  in_progress: "repair",
  pending_parts: "repair",
  repair_complete: "repair",
  awaiting_payment: "invoice",
  awaiting_collection: "delivery",
  closed: "delivery",
};

function RoStageSection({
  label,
  active,
  past,
  live,
  open,
  onRequestExpand,
  children,
}: {
  label: string;
  active: boolean;
  past: boolean;
  live?: boolean;
  open: boolean;
  onRequestExpand: () => void;
  children: React.ReactNode;
}) {
  const upcoming = !active && !past;
  return (
    <div
      data-testid={live ? "ro-stage-section-live" : undefined}
      className={cn(
        "rounded-xl transition-colors",
        live && "border-2 border-amber-400 bg-amber-50/40 shadow-[0_18px_42px_-30px_rgba(180,83,9,0.45)]",
        active && !live && "border-2 border-violet-400/70 bg-white shadow-[0_18px_42px_-30px_rgba(76,29,149,0.55)]",
        !active && past && "border border-slate-200/90 bg-white/85",
        upcoming && "border border-slate-200/90 bg-slate-50/80",
      )}
    >
      <button
        type="button"
        onClick={onRequestExpand}
        className={cn("flex w-full items-center justify-between gap-3 rounded-t-xl px-4 text-left hover:bg-slate-50/80", active ? "py-3.5" : "py-2.5")}
      >
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
          <span className={cn("text-xs font-bold tracking-widest", live && "text-amber-900", active && !live && "text-violet-800", !active && past && "text-slate-800", upcoming && "text-slate-500")}>
            {label.toUpperCase()}
          </span>
          {live ? <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-800">In progress</span> : null}
          {past && !active ? <Check className="h-3.5 w-3.5 shrink-0 text-emerald-600" strokeWidth={3} /> : null}
          {upcoming ? <span className="text-xs text-slate-400">Upcoming</span> : null}
        </div>
        <ChevronDown className={cn("h-4 w-4 shrink-0 text-slate-400 transition-transform", open && "rotate-180")} />
      </button>
      {open ? <div className={cn("space-y-3 border-t border-slate-100 px-4 pb-4", active ? "pt-4" : "pt-3")}>{children}</div> : null}
    </div>
  );
}

// ─── Card wrapper kept so existing intake, diagnosis, repair, finance and collection forms stay mounted ──
function ActiveCard({
  cardId,
  activeCardId,
  dept,
  children,
}: {
  cardId: string;
  activeCardId: string;
  dept: keyof typeof DEPT_COLOURS;
  children: React.ReactNode;
}) {
  const isActive = cardId === activeCardId;
  return (
    <div className="relative">
      {children}
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function JobDetail() {
  const { id } = useParams<{ id: string }>();
  const [, setLocation] = useLocation();
  const { user } = useAuth();
  const historyState = useHistoryState<{ from?: string } | undefined>();
  const jobIdFromRoute = Number.parseInt(id ?? "", 10);
  const hasValidRouteId = Number.isFinite(jobIdFromRoute) && jobIdFromRoute > 0;

  const [editingStatus, setEditingStatus] = useState(false);
  const [editingNote, setEditingNote] = useState(false);
  const [editingETA, setEditingETA] = useState(false);
  const [editingCost, setEditingCost] = useState(false);
  const [editingDiagnosis, setEditingDiagnosis] = useState(false);
  const [editingComplaint, setEditingComplaint] = useState(false);
  const [addingReportEntry, setAddingReportEntry] = useState(false);
  const [newEntryRawNotes, setNewEntryRawNotes] = useState("");
  const [newEntryCustomerReport, setNewEntryCustomerReport] = useState("");
  const [newEntryDate, setNewEntryDate] = useState(() => new Date().toISOString().split("T")[0]);
  const [aiPolishingEntry, setAiPolishingEntry] = useState(false);
  const [resendingNotification, setResendingNotification] = useState(false);
  const [editingQuote, setEditingQuote] = useState(false);
  const [quoteAmount, setQuoteAmount] = useState("");
  const [newStatus, setNewStatus] = useState("");
  const [noteText, setNoteText] = useState("");
  const [estimatedCompletion, setEstimatedCompletion] = useState("");
  const [actualCost, setActualCost] = useState("");
  const [diagnosisText, setDiagnosisText] = useState("");
  const [recommendedRepair, setRecommendedRepair] = useState("");
  const [diagnosisRawNotes, setDiagnosisRawNotes] = useState("");
  const [repairCostInternal, setRepairCostInternal] = useState("");
  const [estimatedWeeks, setEstimatedWeeks] = useState("");
  const [aiPolishing, setAiPolishing] = useState(false);
  const [actingCost, setActingCost] = useState("");
  const [complaintText, setComplaintText] = useState("");
  const [editingCharge, setEditingCharge] = useState(false);
  const [chargeAmount, setChargeAmount] = useState("");
  const [noChargeReason, setNoChargeReason] = useState("");
  const [showNoChargeInput, setShowNoChargeInput] = useState(false);
  const [showUpdateBilling, setShowUpdateBilling] = useState(false);
  const [updateBillingChoice, setUpdateBillingChoice] = useState<"bill" | "no_bill" | null>(null);
  const [updateBillingReason, setUpdateBillingReason] = useState("");
  const [repairJustSaved, setRepairJustSaved] = useState(false);
  const [partsReferToFinance, setPartsReferToFinance] = useState(false);
  const [roInvoiceAmount, setRoInvoiceAmount] = useState("");
  const [roInvoiceDescription, setRoInvoiceDescription] = useState("");
  const [roSendChannel, setRoSendChannel] = useState<"email" | "whatsapp">("email");
  const [openStageId, setOpenStageId] = useState<RoFocusedStageId>("intake");

  const invoiceInputRef = useRef<HTMLInputElement>(null);

  const { data, error, refetch } = trpc.jobs.getById.useQuery(
    { id: jobIdFromRoute },
    { enabled: hasValidRouteId }
  );
  const myActiveTimers = trpc.roWorkSessions.myActive.useQuery(undefined, {
    enabled: Boolean(user) && hasValidRouteId,
    refetchInterval: 15_000,
  });
  useEffect(() => {
    console.info("[routing-check] entity=repair-order routeId=%s parsedId=%s valid=%s", id, jobIdFromRoute, hasValidRouteId);
  }, [id, jobIdFromRoute, hasValidRouteId]);
  useEffect(() => {
    if (data?.job?.id) {
      console.info("[routing-check] entity=repair-order id=%s fetch=success", data.job.id);
    } else if (error) {
      console.warn("[routing-check] entity=repair-order id=%s fetch=failure", jobIdFromRoute, error.message);
    }
  }, [data?.job?.id, error, jobIdFromRoute]);
  useEffect(() => {
    if (!data?.job?.status) return;
    setOpenStageId(roFocusedStageId(data.job.status));
  }, [data?.job?.status]);

  // Pre-populate invoice amount from existing invoice when data loads
  useEffect(() => {
    const amount = (data?.job as any)?.roInvoiceAmount;
    if (amount && !roInvoiceAmount) {
      setRoInvoiceAmount(Number(amount).toFixed(2));
    }
  }, [data?.job]);

  const updateStatus = trpc.jobs.updateStatus.useMutation({
    onSuccess: () => { toast.success("Status updated"); setEditingStatus(false); refetch(); },
    onError: (e) => toast.error(e.message),
  });

  const updateJob = trpc.jobs.update.useMutation({
    onSuccess: () => { toast.success("Saved"); setEditingETA(false); setEditingCost(false); setEditingComplaint(false); refetch(); },
    onError: (e) => toast.error(e.message),
  });

  const addNote = trpc.jobs.addNote.useMutation({
    onSuccess: () => { toast.success("Note added"); setEditingNote(false); setNoteText(""); refetch(); },
    onError: (e) => toast.error(e.message),
  });

  const saveDiagnosis = trpc.jobs.saveDiagnosis.useMutation({
    onSuccess: (result) => {
      if (result.notify?.ok === false) {
        toast.error(result.notify.error || "Diagnosis saved, but Admin/Finance notification failed. Use Retry.");
      } else {
        toast.success("Diagnosis saved — awaiting commercial review");
      }
      setEditingDiagnosis(false);
      refetch();
    },
    onError: (e) => toast.error(e.message),
  });

  const aiPolish = trpc.jobs.aiPolish.useMutation();

  const slaWait = trpc.jobs.slaWait.useMutation({
    onSuccess: () => { toast.success("SLA extended by 3 working days"); refetch(); },
    onError: (e) => toast.error(e.message),
  });

  const { data: reportEntries = [], refetch: refetchEntries } = trpc.jobs.getReportEntries.useQuery(
    { jobId: jobIdFromRoute },
    { enabled: hasValidRouteId }
  );

  const addReportEntry = trpc.jobs.addReportEntry.useMutation({
    onSuccess: () => {
      toast.success("Repair entry saved — ready to send to Finance");
      setAddingReportEntry(false);
      setNewEntryRawNotes("");
      setNewEntryCustomerReport("");
      setNewEntryDate(new Date().toISOString().split("T")[0]);
      setRepairJustSaved(true);
      refetchEntries();
      refetch(); // refresh job status so Send to Finance button appears
    },
    onError: (e) => toast.error(e.message),
  });

  const deleteReportEntry = trpc.jobs.deleteReportEntry.useMutation({
    onSuccess: () => { toast.success("Entry deleted"); refetchEntries(); },
    onError: (e) => toast.error(e.message),
  });

  const resendIntakeNotification = trpc.jobs.resendIntakeNotification.useMutation({
    onSuccess: (result) => {
      const channels = [result.email && "email", result.whatsapp && "WhatsApp"].filter(Boolean).join(" & ");
      toast.success(`Intake notification resent${channels ? ` via ${channels}` : ""}`);
      setResendingNotification(false);
    },
    onError: (err) => { toast.error(`Failed to resend: ${err.message}`); setResendingNotification(false); },
  });

  // ─── Admin: Change RO PIC ───────────────────────────────────────────────
  const [changingPic, setChangingPic] = useState(false);
  const [newPicId, setNewPicId] = useState("");
  const { data: picCandidates } = trpc.customers.listSalesStaff.useQuery(undefined, {
    enabled: user?.role === "admin" && changingPic,
  });
  const changePic = trpc.jobs.changePic.useMutation({
    onSuccess: (result) => {
      toast.success(result.changed ? `PIC changed to ${result.salespersonName}` : "PIC unchanged");
      setChangingPic(false);
      setNewPicId("");
      refetch();
    },
    onError: (err) => toast.error(err.message),
  });

  const setQuote = trpc.jobs.setQuote.useMutation({
    onSuccess: () => { toast.success("Customer selling price saved. The PIC can approve on behalf of the customer or send an approval request."); setEditingQuote(false); refetch(); },
    onError: (err) => toast.error(err.message),
  });
  const sendRepairProposal = trpc.jobs.sendRepairProposalToCustomer.useMutation({
    onSuccess: (result) => {
      if (result.alreadySent) toast.message("Proposal already sent — no duplicate email.");
      else if (result.sent) toast.success("Proposal emailed to the customer.");
      else toast.error(result.error || "Proposal email failed. Use Retry.");
      refetch();
    },
    onError: (err) => toast.error(err.message),
  });
  const declineOnBehalf = trpc.jobs.declineRepairOnBehalf.useMutation({
    onSuccess: (result) => { toast.success("NOT APPROVED recorded. PIC follow-up is required."); if (!result.notify.ok) toast.warning("Decision saved; PIC notification needs attention."); refetch(); },
  });
  const bypassWork = trpc.roWorkSessions.startWithoutApproval.useMutation();
  const approveOnBehalf = trpc.jobs.approveRepairOnBehalf.useMutation({
    onSuccess: (result) => {
      if (result.alreadyApproved) toast.message("Repair already approved.");
      else toast.success("Approval recorded. The technician may start repair work.");
      if (!result.notify.ok) toast.warning("Approval is saved, but staff notification needs attention. Check technician assignment and retry notifications.");
      refetch();
    },
    onError: (err) => { toast.error(err.message); refetch(); },
  });
  const retryRepairProposalNotify = trpc.jobs.retryRepairProposalNotify.useMutation({
    onSuccess: () => { toast.success("Retry queued."); refetch(); },
    onError: (err) => toast.error(err.message),
  });

  const uploadInvoice = trpc.jobs.uploadInvoice.useMutation({
    onSuccess: () => { toast.success("Invoice uploaded and customer notified"); refetch(); },
    onError: (e) => toast.error(e.message),
  });

  const resendInvoice = trpc.jobs.resendInvoice.useMutation({
    onSuccess: (result) => {
      const channels = [result.email && "email", result.whatsapp && "WhatsApp"].filter(Boolean).join(" & ");
      toast.success(`Invoice notification resent${channels ? ` via ${channels}` : ""}`);
    },
    onError: (e) => toast.error(e.message),
  });

  const generateROInvoice = trpc.jobs.generateROInvoice.useMutation({
    onSuccess: () => { toast.success("Invoice generated with payment link & QR code!"); setRoInvoiceAmount(""); setRoInvoiceDescription(""); refetch(); },
    onError: (e) => toast.error(e.message),
  });
  const confirmROInvoice = trpc.jobs.confirmROInvoice.useMutation({
    onSuccess: () => { toast.success("Invoice confirmed."); refetch(); },
    onError: (e) => toast.error(e.message),
  });
  const sendROInvoice = trpc.jobs.sendROInvoice.useMutation({
    onSuccess: () => { toast.success("Invoice sent to customer!"); refetch(); },
    onError: (e) => toast.error(e.message),
  });
  const markROPaid = trpc.jobs.markROPaid.useMutation({
    onSuccess: () => { toast.success("Invoice marked as paid — job moved to Awaiting Collection."); refetch(); },
    onError: (e) => toast.error(e.message),
  });
  const adminSetNoCharge = trpc.jobs.adminSetNoCharge.useMutation({
    onSuccess: (result) => {
      toast.success(result.closed
        ? "Marked as no charge — RO closed."
        : "Marked as non-billable — repair status unchanged.");
      setShowNoChargeInput(false);
      setNoChargeReason("");
      refetch();
    },
    onError: (e) => toast.error(e.message),
  });
  const adminSetCharge = trpc.jobs.adminSetCharge.useMutation({
    onSuccess: (result) => {
      if (!result.notify.ok) toast.warning("Price saved; PIC notification needs attention.");
      toast.success("Customer selling price confirmed. The PIC can record approval or send a customer approval request.");
      setEditingCharge(false);
      setEditingCost(false);
      setChargeAmount("");
      refetch();
    },
    onError: (e) => toast.error(e.message),
  });

  const [financePartsCost, setFinancePartsCost] = useState("");
  const resolveFinanceReferral = trpc.jobs.resolveFinanceReferral.useMutation({
    onSuccess: () => { toast.success("Parts cost saved — awaiting commercial review of the customer selling price."); setFinancePartsCost(""); refetch(); },
    onError: (e) => toast.error(e.message),
  });

  const markCollected = trpc.jobs.markCollected.useMutation({
    onSuccess: () => { toast.success("Item marked as collected — RO is now closed."); refetch(); },
    onError: (e) => toast.error(e.message),
  });

  const deleteJob = trpc.jobs.deleteJob.useMutation({
    onSuccess: () => { toast.success("Repair Order deleted"); setLocation("/repair-orders"); },
    onError: (e) => toast.error(e.message),
  });

  const handlePolishEntryNotes = async () => {
    if (!newEntryRawNotes.trim()) return;
    setAiPolishingEntry(true);
    try {
      const result = await aiPolish.mutateAsync({ text: newEntryRawNotes, type: "service_report" });
      setNewEntryCustomerReport(String(result.polished));
    } catch (e: any) { toast.error(e?.message || "AI polish failed"); }
    finally { setAiPolishingEntry(false); }
  };

  const handlePolishDiagnosis = async () => {
    const source = diagnosisRawNotes.trim() || diagnosisText.trim();
    if (!source) return;
    setAiPolishing(true);
    try {
      const result = await aiPolish.mutateAsync({ text: source, type: "diagnosis" });
      setDiagnosisText(String(result.polished));
    } catch (e: any) { toast.error(e?.message || "AI polish failed"); }
    finally { setAiPolishing(false); }
  };

  const handleInvoiceUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async () => {
      const base64 = (reader.result as string).split(",")[1];
      await uploadInvoice.mutateAsync({ jobId: job.id, filename: file.name, base64 });
    };
    reader.readAsDataURL(file);
  };

  if (!hasValidRouteId) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-center text-muted-foreground">
            <p className="text-sm">Invalid repair order ID.</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-center text-muted-foreground">
            <p className="text-sm">Unable to load repair order.</p>
            <p className="text-xs mt-1">{error.message}</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (!data) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-center text-muted-foreground">
            <div className="h-8 w-8 border-2 border-gray-300 border-t-black rounded-full animate-spin mx-auto mb-3" />
            <p className="text-sm">Loading job...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const { job, customer, product, photos, notes, auditLogs, flags, workflow } = data;
  const isAdmin = user?.role === "admin";
  const isService = user?.role === "service" || isAdmin;
  const isFinance = user?.role === "finance" || isAdmin;
  const isSales = user?.role === "sales" || isAdmin;
  const commercialViewer = user
    ? {
        userId: user.id,
        role: user.role,
        secondaryRolesJson: (user as { secondaryRolesJson?: unknown }).secondaryRolesJson,
      }
    : null;
  const ownTimer = (myActiveTimers.data ?? []).find((row) => row.repairOrderId === job.id) ?? null;
  const ownTimerRunning = ownTimer != null;
  const liveWork = roLiveWorkPresentation(job.status, ownTimerRunning);
  const showSuggestedRepairCost = canShowSuggestedRepairCost(commercialViewer, {
    assignedTechnicianId: job.assignedTechnicianId ?? null,
  });
  const canSeeActivity = isAdmin || isService || isFinance;
  const latestReport = reportEntries[0]?.customerReport?.trim() || (job as { serviceReport?: string | null }).serviceReport?.trim() || "";
  const equipmentTitle = [product?.brand, product?.model].filter(Boolean).join(" ") || "Equipment not recorded";
  const isClosed = job.status === "closed";

  // Which department section is currently active?
  // Smart override: if diagnosis text exists but status is still 'received', treat as 'diagnosis'
  const effectiveStatus = (job.status === "received" && (job as any).diagnosedFault)
    ? "diagnosis"
    : job.status;
  // repairApprovedAt means the job is approved — the repair stage shows the approval banner
  const repairApprovedAt = (job as any).repairApprovedAt;
  const repairApprovedBy = (job as any).repairApprovedBy;

  // Which specific card should the triangle point at?
  // If repair is approved and we're in in_progress/pending_parts, point at the approval banner
  const rawActiveCard = STATUS_CARD[effectiveStatus] ?? "customer";
  const activeCardId = (repairApprovedAt && ["in_progress", "pending_parts", "repair_complete"].includes(job.status))
    ? "approved"
    : rawActiveCard;

  // customerCharge set by admin
  const customerCharge = (job as any).customerCharge;
  const proposalCustomerSend = repairProposalCustomerSendState({
    customerId: job.customerId,
    customerEmail: customer?.email,
  });

  // Tracking URL for this job
  const trackingUrl = (job as any).trackingToken
    ? `${window.location.origin}/track/${(job as any).trackingToken}`
    : null;

  // SLA info
  const slaDeadline = (job as any).slaExtendedUntil || (job as any).slaDeadline;
  const slaDaysLeft = slaDeadline
    ? Math.ceil((new Date(slaDeadline).getTime() - Date.now()) / (1000 * 60 * 60 * 24))
    : null;
  const slaOverdue = slaDaysLeft !== null && slaDaysLeft < 0;
  const slaWarning = slaDaysLeft !== null && slaDaysLeft <= 1 && !slaOverdue;
  const closedStatuses = ["repair_complete", "awaiting_payment", "awaiting_collection", "closed"];
  const genericStatusOptions = genericStatusUpdateOptions(job.status, JOB_STATUS_ORDER);
  const focused = buildRoFocusedWorkflow({
    status: job.status,
    assignedTechnicianId: job.assignedTechnicianId ?? null,
    assignedTechnicianName: (job as { assignedTechnicianName?: string | null }).assignedTechnicianName,
    viewer: user ? { userId: user.id, role: user.role, secondaryRolesJson: (user as { secondaryRolesJson?: unknown }).secondaryRolesJson } : null,
    ownTimerRunning,
  });
  const spineIndex = focused.workflowComplete ? RO_FOCUSED_STAGES.length : RO_FOCUSED_STAGES.findIndex((stage) => stage.id === focused.stageId);
  const stageUi = (id: RoFocusedStageId) => {
    const index = RO_FOCUSED_STAGES.findIndex((stage) => stage.id === id);
    return { active: index === spineIndex, past: index < spineIndex };
  };
  const technicianName = (job as { assignedTechnicianName?: string | null }).assignedTechnicianName?.trim() || "";
  const headerMeta = [customer?.name, equipmentTitle, product?.serialNumber ? `S/N ${product.serialNumber}` : null, technicianName || "Unassigned"]
    .filter((part): part is string => Boolean(part && part.trim() && part !== "Equipment not recorded"));
  const scrollToStage = (stageId: RoFocusedStageId) => {
    setOpenStageId(stageId);
    window.setTimeout(() => document.getElementById(stageId)?.scrollIntoView({ behavior: "smooth", block: "start" }), 40);
  };
  const timerStartMode = focused.primaryKind === "start" || focused.primaryKind === "claim" || focused.primaryKind === "admin-start"
    ? focused.primaryKind
    : "hidden";

  return (
    <DashboardLayout>
      <div className="min-h-[calc(100vh-4rem)] w-full bg-slate-100/60">
        <div className="border-b border-slate-200/80 bg-white px-4 py-4 md:px-8">
          <button
            type="button"
            data-testid="ro-back"
            onClick={() => {
              const fromHist =
                historyState && typeof historyState === "object" && historyState !== null && "from" in historyState
                  ? (historyState as { from?: string }).from
                  : undefined;
              if (typeof fromHist === "string" && fromHist.length > 0) {
                setLocation(fromHist);
                return;
              }
              try {
                const ref = sessionStorage.getItem("jobDetailReferrer");
                if (ref) {
                  sessionStorage.removeItem("jobDetailReferrer");
                  setLocation(ref);
                  return;
                }
              } catch {
                /* ignore */
              }
              setLocation("/repair-orders");
            }}
            className="flex items-center gap-1 text-sm text-slate-500 transition-colors hover:text-slate-800"
          >
            <ChevronLeft className="h-4 w-4" />
            Repair Orders
          </button>
          <div className="mt-4 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0">
              <h1 data-testid="ro-reference" className="font-mono text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">{job.jobNumber}</h1>
              <p data-testid="ro-header-meta" className="mt-2 text-sm text-slate-500">
                {headerMeta.map((part, index) => (
                  <span key={`${part}-${index}`}>
                    {index > 0 ? " · " : null}
                    {customer?.id && part === customer.name ? (
                      <button type="button" className="hover:underline" onClick={() => setLocation(`/customers/id/${customer.id}`)}>{part}</button>
                    ) : part}
                  </span>
                ))}
              </p>
            </div>
            <div className="flex shrink-0 flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => { void navigator.clipboard.writeText(job.jobNumber); toast.success("Repair order number copied"); }}
                className="inline-flex h-8 w-8 items-center justify-center rounded-md text-slate-500 transition hover:bg-slate-100 hover:text-slate-800"
                title="Copy repair order number"
              >
                <Copy className="h-4 w-4" />
              </button>
              <span data-testid="ro-status-badge" className="inline-flex items-center rounded-full bg-violet-100 px-3 py-1.5 text-xs font-medium text-violet-800">
                {focused.headerBadge}
              </span>
              {(job as { noCharge?: boolean }).noCharge ? (
                <span className="inline-flex items-center gap-1 rounded-full border border-green-200 bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700">
                  <CheckCircle2 className="h-3 w-3" />
                  No charge
                </span>
              ) : null}
              {isService && !isClosed ? (
                editingStatus ? (
                  <div className="flex items-center gap-1">
                    <select value={newStatus} onChange={(e) => setNewStatus(e.target.value)} className="rounded-md border border-input bg-background px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-ring">
                      <option value="">Select status…</option>
                      {genericStatusOptions.map((s) => (
                        <option key={s} value={s}>{JOB_STATUS_LABELS[s]}</option>
                      ))}
                    </select>
                    <Button size="sm" className="h-7 text-xs" onClick={() => { if (newStatus) updateStatus.mutate({ id: job.id, status: newStatus as never }); }} disabled={!newStatus || updateStatus.isPending}>
                      <Save className="mr-1 h-3 w-3" />Save
                    </Button>
                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setEditingStatus(false)}>Cancel</Button>
                  </div>
                ) : (
                  <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => { setNewStatus(job.status); setEditingStatus(true); }}>
                    <Pencil className="mr-1 h-3 w-3" />Update Status
                  </Button>
                )
              ) : null}
              {isAdmin ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button type="button" variant="outline" size="icon" className="h-8 w-8 shrink-0" aria-label="More actions">
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem
                      className="text-destructive focus:text-destructive"
                      disabled={deleteJob.isPending}
                      onClick={() => {
                        if (window.confirm(`Permanently delete ${job.jobNumber}? This cannot be undone.`)) deleteJob.mutate({ id: job.id });
                      }}
                    >
                      Delete repair order
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : null}
            </div>
          </div>
          {(flags.isAging || flags.warrantyLapsed || (slaDeadline && !closedStatuses.includes(job.status))) ? (
            <div className="mt-3 flex flex-wrap items-center gap-2">
              {flags.isAging ? (
                <span className="inline-flex items-center gap-1 rounded-full border border-red-200 bg-red-50 px-2 py-0.5 text-xs font-medium text-red-600">
                  <AlertTriangle className="h-3 w-3" />Aging ({flags.daysSinceUpdate}d)
                </span>
              ) : null}
              {flags.warrantyLapsed ? (
                <span className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-600">
                  <AlertTriangle className="h-3 w-3" />Warranty lapsed
                </span>
              ) : null}
              {slaDeadline && !closedStatuses.includes(job.status) ? (
                <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium ${slaOverdue ? "border-red-200 bg-red-50 text-red-600" : slaWarning ? "border-amber-200 bg-amber-50 text-amber-600" : "border-green-200 bg-green-50 text-green-700"}`}>
                  <Clock className="h-3 w-3" />
                  {slaOverdue ? `SLA overdue ${Math.abs(slaDaysLeft!)}d` : `SLA ${slaDaysLeft}d left`}
                </span>
              ) : null}
              {isAdmin && slaOverdue && slaDeadline && !closedStatuses.includes(job.status) ? (
                <Button size="sm" variant="outline" className="h-6 px-2 text-xs" onClick={() => slaWait.mutate({ id: job.id })} disabled={slaWait.isPending}>WAIT +3d</Button>
              ) : null}
            </div>
          ) : null}
          <div className="mt-5 border-t border-slate-100 pt-4 md:hidden" data-testid="ro-focused-workflow-mobile">
            <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">Focused Workflow</p>
            <div className="flex items-stretch justify-between gap-0.5 overflow-x-auto pb-1">
              {RO_FOCUSED_STAGES.map((stage, idx) => {
                const isPast = idx < spineIndex;
                const isCurrent = idx === spineIndex;
                const isLive = isCurrent && liveWork.active;
                return (
                  <button key={stage.id} type="button" onClick={() => scrollToStage(stage.id)} className="flex min-w-0 flex-1 flex-col items-center gap-1.5 border-l border-slate-100 pl-1 first:border-l-0 first:pl-0">
                    <span className={cn("flex h-6 w-6 items-center justify-center rounded-full text-[10px] font-semibold", isLive && "bg-amber-500 text-white ring-4 ring-amber-100", isCurrent && !isLive && "bg-violet-600 text-white", isPast && "bg-emerald-500 text-white", !isCurrent && !isPast && "bg-slate-100 text-slate-500")}>
                      {isPast ? <Check className="h-3 w-3" strokeWidth={3} /> : idx + 1}
                    </span>
                    <span className={cn("w-full text-center text-[9px] font-bold leading-tight", isPast && "text-emerald-800", isLive && "text-amber-800", isCurrent && !isLive && "text-violet-800", !isPast && !isCurrent && "text-slate-400")}>{stage.label}</span>
                    {isLive ? <RoLiveWorkMark live={liveWork} /> : null}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <div className="mx-auto flex w-full max-w-[1560px] items-start">
          <div className="hidden w-[300px] min-w-[300px] shrink-0 self-stretch border-r border-slate-200/80 bg-white md:block">
            <div className="sticky top-0 px-5 py-7">
              <p className="mb-4 px-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">Focused Workflow</p>
              <div data-testid="ro-focused-workflow" className="relative">
                {RO_FOCUSED_STAGES.map((stage, idx) => {
                  const isPast = idx < spineIndex;
                  const isCurrent = idx === spineIndex;
                  const isLive = isCurrent && liveWork.active;
                  return (
                    <div key={stage.id} className={cn("relative", isLive && "rounded-xl bg-amber-50/80")}>
                      {idx < RO_FOCUSED_STAGES.length - 1 ? <div className="absolute left-[15px] top-8 h-[calc(100%-8px)] w-px bg-slate-200" aria-hidden /> : null}
                      <button type="button" onClick={() => scrollToStage(stage.id)} className="relative flex w-full items-start gap-3 px-1 py-2.5 text-left">
                        <span className={cn("z-[1] flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-semibold", isLive && "bg-amber-500 text-white ring-4 ring-amber-100", isCurrent && !isLive && "bg-violet-600 text-white", isPast && "bg-emerald-500 text-white", !isCurrent && !isPast && "bg-slate-100 text-slate-500")}>
                          {isPast ? <Check className="h-3.5 w-3.5" strokeWidth={3} /> : idx + 1}
                        </span>
                        <span className="min-w-0 pt-0.5">
                          <span className="flex flex-wrap items-center gap-2">
                            <span className={cn("block text-sm font-semibold", isLive ? "text-amber-950" : isCurrent ? "text-violet-800" : "text-slate-800")}>{stage.label}</span>
                            {isLive ? <RoLiveWorkMark live={liveWork} /> : null}
                          </span>
                          <span className="mt-0.5 block text-xs leading-snug text-slate-500">{stage.description}</span>
                          {isPast ? <span className="mt-1 block text-[11px] text-emerald-700">Completed</span> : null}
                          {isCurrent ? <span data-testid="ro-current-stage" className={cn("mt-1 block text-[11px] font-semibold uppercase tracking-wide", isLive ? "text-amber-800" : "text-violet-700")}>{focused.currentStage}</span> : null}
                        </span>
                      </button>
                    </div>
                  );
                })}
                <p className="mt-4 flex items-start gap-1.5 text-[10px] leading-relaxed text-slate-500">
                  <Info className="mt-0.5 h-3 w-3 shrink-0" />
                  <span>{focused.workflowComplete ? "This repair order is completed — all stages including Collection / Close are done." : "The highlighted stage is the current step. Select a stage to jump to its actions."}</span>
                </p>
              </div>
            </div>
          </div>

          <div className="min-w-0 flex-1 space-y-5 bg-slate-50/70 py-6 pl-4 pr-4 sm:px-6 md:px-8 lg:px-10">
            {["awaiting_commercial_review", "ready_to_send_to_customer", "awaiting_customer_confirmation", "customer_declined"].includes(job.status) ? (
              <RepairNextAction
                key={`${job.id}:${job.status}:${job.currentProposalVersion ?? 0}:${ownTimer?.sessionId ?? 0}`}
                status={job.status} actorName={user?.name || "You"}
                picName={workflow?.nextAction?.picName || job.salespersonName || "Assigned PIC"}
                technicianName={workflow?.nextAction?.technicianName || technicianName}
                canSetPrice={Boolean(workflow?.canConfirmSellingPrice)} canDecide={Boolean(workflow?.nextAction?.canDecide)}
                canBypass={Boolean(workflow?.nextAction?.canBypass) && !ownTimerRunning}
                guideCost={showSuggestedRepairCost ? job.repairCostInternal : null}
                confirmedPrice={workflow?.proposalPreview?.sellingPrice} proposalVersion={job.currentProposalVersion ?? 0}
                sendBlocker={workflow?.nextAction?.sendBlocker} decisionBlocker={workflow?.nextAction?.decisionBlocker}
                notificationIncomplete={workflow?.notifyOutbox?.some(row => ["pic_ready", "decline_pic"].includes(row.kind) && ["failed", "pending"].includes(row.status))}
                onConfirmPrice={async amount => { await adminSetCharge.mutateAsync({ jobId: job.id, customerCharge: amount }); await refetch(); }}
                onDecision={async (approved, reason) => { const input = { jobId: job.id, proposalVersion: job.currentProposalVersion!, reason }; if (approved) await approveOnBehalf.mutateAsync(input); else await declineOnBehalf.mutateAsync(input); await refetch(); }}
                onSendRequest={async () => { const result = await sendRepairProposal.mutateAsync({ jobId: job.id, origin: window.location.origin }); if (!result.sent) throw new Error(result.error || "The request was not sent."); await refetch(); }}
                onBypass={async reason => { await bypassWork.mutateAsync({ jobId: job.id, sessionUuid: crypto.randomUUID(), reason, expectedStatus: job.status, expectedVersion: job.currentProposalVersion ?? 0 }); await Promise.all([refetch(), myActiveTimers.refetch()]); }}
                onRetry={async () => { await retryRepairProposalNotify.mutateAsync({ jobId: job.id }); await refetch(); }}
              >
                {ownTimerRunning ? <><p className="mb-3 text-sm font-medium text-amber-200">Work timer running while customer approval remains outstanding. This is not customer approval.</p><RoWorkTimerCard variant="next-action" startMode="hidden" jobId={job.id} jobNumber={job.jobNumber} status={job.status} assignedTechnicianId={job.assignedTechnicianId ?? null} currentUserId={user?.id ?? null} isAdmin={isAdmin} /></> : null}
              </RepairNextAction>
            ) : (
            <section data-testid="ro-next-action" className="rounded-2xl bg-[#0f1c3f] px-5 py-5 text-white shadow-sm sm:px-6">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                <div className="min-w-0">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-300">{focused.nextEyebrow}</p>
                  <p className="mt-2 text-xs text-slate-300">Current stage · {focused.stageLabel}</p>
                  <h2 className="mt-1 text-xl font-semibold tracking-tight sm:text-2xl">{focused.nextTitle}</h2>
                  <p className="mt-2 max-w-2xl text-sm leading-relaxed text-slate-200">{focused.nextDetail}</p>
                  {focused.completed.length > 0 && !focused.workflowComplete ? (
                    <p className="mt-3 text-xs text-slate-300">Done: {focused.completed.join(" · ")}</p>
                  ) : null}
                  {focused.blocker ? <p data-testid="ro-blocker" className="mt-3 text-sm font-medium text-amber-200">{focused.blocker}</p> : null}
                </div>
                <div className="flex shrink-0 flex-col items-stretch gap-2 sm:items-end">
                  <RoWorkTimerCard
                    variant="next-action"
                    startMode={ownTimerRunning ? "hidden" : timerStartMode}
                    jobId={job.id}
                    jobNumber={job.jobNumber}
                    status={job.status}
                    assignedTechnicianId={job.assignedTechnicianId ?? null}
                    currentUserId={user?.id ?? null}
                    isAdmin={isAdmin}
                  />
                  {focused.primaryLabel && focused.primaryKind !== "start" && focused.primaryKind !== "claim" && focused.primaryKind !== "admin-start" ? (
                    <Button
                      type="button"
                      data-testid="ro-primary-action"
                      className="h-11 shrink-0 rounded-xl bg-white px-4 text-sm font-semibold tracking-wide text-slate-900 hover:bg-slate-100"
                      onClick={() => {
                        const target = focused.primaryKind === "focus-finance" ? "finance"
                          : focused.primaryKind === "focus-collection" ? "collection"
                          : focused.primaryKind === "focus-diagnosis" ? "diagnosis"
                          : "approval";
                        scrollToStage(target);
                      }}
                    >
                      {focused.primaryLabel}
                    </Button>
                  ) : null}
                </div>
              </div>
            </section>
            )}

            <div data-testid="ro-summary-grid" className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <section data-testid="ro-customer-equipment" className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">Customer &amp; equipment</p>
                <p className="mt-3 text-lg font-semibold text-slate-900">
                  {customer?.id ? (
                    <button type="button" className="hover:underline" onClick={() => setLocation(`/customers/id/${customer.id}`)}>{customer.name}</button>
                  ) : (customer?.name || "—")}
                </p>
                <dl className="mt-3 space-y-2 text-sm">
                  <div className="flex items-start justify-between gap-4"><dt className="text-slate-500">Equipment</dt><dd className="text-right font-medium text-slate-900">{equipmentTitle}</dd></div>
                  <div className="flex items-start justify-between gap-4"><dt className="text-slate-500">Serial number</dt><dd className="text-right font-mono font-medium text-slate-900">{product?.serialNumber || "—"}</dd></div>
                  <div className="flex items-start justify-between gap-4"><dt className="text-slate-500">Warranty</dt><dd className="text-right font-medium text-slate-900">{job.warrantyAtIntake === "yes" ? "Under warranty" : "Not under warranty"}{(job as { noCharge?: boolean }).noCharge ? " · Non-billable" : ""}</dd></div>
                  <div className="flex items-start justify-between gap-4"><dt className="text-slate-500">Assigned technician</dt><dd className="text-right font-medium text-slate-900">{technicianName || "Unassigned"}</dd></div>
                </dl>
              </section>
              <section data-testid="ro-repair-summary" className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">Repair summary</p>
                <dl className="mt-3 space-y-2 text-sm">
                  <div className="flex items-start justify-between gap-4"><dt className="text-slate-500">Repair state</dt><dd className="text-right font-medium text-slate-900">{JOB_STATUS_LABELS[job.status] ?? job.status}</dd></div>
                  <div className="flex items-start justify-between gap-4"><dt className="text-slate-500">Work recorded</dt><dd className="text-right"><RoWorkRecordedState live={liveWork} recordedLabel={recordedWorkLabel(auditLogs ?? [], ownTimerRunning)} technicianName={ownTimer?.technicianName} startedLabel={ownTimer ? formatDateTime(ownTimer.startedAt) : null} /></dd></div>
                  {(job as { diagnosedFault?: string | null }).diagnosedFault ? (
                    <div><dt className="text-slate-500">Diagnosis</dt><dd className="mt-1 whitespace-pre-wrap text-slate-800">{(job as { diagnosedFault?: string | null }).diagnosedFault}</dd></div>
                  ) : null}
                  {showSuggestedRepairCost && (job as { repairCostInternal?: string | number | null }).repairCostInternal ? (
                    <div data-testid="ro-suggested-cost" className="flex items-start justify-between gap-4"><dt className="text-slate-500">Suggested repair cost</dt><dd className="text-right font-medium text-slate-900">S${(job as { repairCostInternal?: string | number | null }).repairCostInternal}</dd></div>
                  ) : null}
                </dl>
              </section>
            </div>

            <section data-testid="ro-reported-fault" className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
              <div className="flex items-center gap-2 text-slate-800">
                <ClipboardList className="h-4 w-4" />
                <h2 className="text-base font-semibold">Reported fault</h2>
              </div>
              <p className="mt-1 text-xs text-slate-400">Read before starting</p>
              <p data-testid="ro-fault-body" className="mt-4 whitespace-pre-wrap text-[15px] leading-7 text-slate-800">
                {job.faultReported || <span className="italic text-slate-400">Not specified</span>}
              </p>
            </section>

            {canSeeActivity ? (
              <section data-testid="ro-activity-card" className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="flex items-center justify-between gap-3">
                  <h2 className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">Activity</h2>
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Server recorded</p>
                </div>
                <RoActivityTimeline entries={auditLogs ?? []} formatDateTime={formatDateTime} />
              </section>
            ) : null}

        {/* ════════════════════════════════════════════════════════════════════
            SECTION 1 — INTAKE (PIC)
            Covers: received
        ════════════════════════════════════════════════════════════════════ */}
        <div id="intake" data-testid="ro-stage-intake" className="scroll-mt-24">
          <RoStageSection
            label="Intake"
            active={stageUi("intake").active}
            past={stageUi("intake").past}
            open={openStageId === "intake"}
            onRequestExpand={() => setOpenStageId("intake")}
          >

            {/* Customer + Product — 2-col grid */}
            <ActiveCard cardId="customer" activeCardId={activeCardId} dept="intake">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Customer */}
              <Card className="border-slate-200 shadow-sm">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <User className="h-4 w-4" />Customer
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 text-sm">
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Name</p>
                    <button className="font-medium hover:underline text-left"
                      onClick={() => customer && setLocation(`/customers/id/${customer.id}`)}>
                      {customer?.name || "—"}
                    </button>
                  </div>
                  {customer?.contact && (
                    <div><p className="text-muted-foreground text-xs">Contact</p><p className="font-medium">{customer.contact}</p></div>
                  )}
                  {customer?.email && (
                    <div><p className="text-muted-foreground text-xs">Email</p><p className="font-medium">{customer.email}</p></div>
                  )}
                  {customer?.address && (
                    <div><p className="text-muted-foreground text-xs">Address</p><p className="font-medium">{customer.address}</p></div>
                  )}
                  <div>
                    <p className="text-muted-foreground text-xs">Collection</p>
                    <p className="font-medium">{job.collectionPreference === "delivery" ? "Delivery" : "Self Pick-Up"}</p>
                  </div>
                  {(job.salespersonName || isAdmin) && (
                    <div>
                      <p className="text-muted-foreground text-xs">PIC</p>
                      <div className="flex items-center justify-between gap-2">
                        <p className="font-medium">{job.salespersonName || "Unassigned"}</p>
                        {isAdmin && !changingPic && (
                          <Button size="sm" variant="ghost" className="h-6 px-2 text-xs gap-1"
                            onClick={() => { setNewPicId(job.salespersonId ? String(job.salespersonId) : ""); setChangingPic(true); }}>
                            <Pencil className="h-3 w-3" /> Change PIC
                          </Button>
                        )}
                      </div>
                      {isAdmin && changingPic && (
                        <div className="mt-2 space-y-2">
                          <select
                            aria-label="New PIC"
                            value={newPicId}
                            onChange={(e) => setNewPicId(e.target.value)}
                            className="w-full border border-input rounded-md px-2 py-1.5 text-sm bg-background"
                          >
                            <option value="">Select a Sales/Admin PIC...</option>
                            {(picCandidates ?? []).map((s) => (
                              <option key={s.id} value={s.id}>{s.name ?? "(no name)"}</option>
                            ))}
                          </select>
                          <p className="text-[11px] text-muted-foreground">Only active Sales/Admin users can be PIC. Technicians/installers are never PIC.</p>
                          <div className="flex gap-2">
                            <Button size="sm" className="h-7 text-xs"
                              disabled={!newPicId || changePic.isPending}
                              onClick={() => changePic.mutate({ jobId: job.id, salespersonId: Number(newPicId) })}>
                              {changePic.isPending ? "Saving…" : "Save PIC"}
                            </Button>
                            <Button size="sm" variant="outline" className="h-7 text-xs"
                              disabled={changePic.isPending}
                              onClick={() => { setChangingPic(false); setNewPicId(""); }}>
                              Cancel
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  <div className="pt-2 border-t">
                    <Button size="sm" variant="outline" className="w-full text-xs gap-1"
                      disabled={resendingNotification || resendIntakeNotification.isPending}
                      onClick={() => { setResendingNotification(true); resendIntakeNotification.mutate({ jobId: Number(id), origin: window.location.origin }); }}>
                      <Bell className="h-3 w-3" />
                      {resendIntakeNotification.isPending ? "Sending…" : "Resend Intake Receipt"}
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Product */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-2">
                    <Wrench className="h-4 w-4" />Product
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                  <div><p className="text-muted-foreground text-xs">Brand</p><p className="font-medium">{product?.brand || "—"}</p></div>
                  <div><p className="text-muted-foreground text-xs">Model</p><p className="font-medium">{product?.model || "—"}</p></div>
                  <div>
                    <p className="text-muted-foreground text-xs">Serial Number</p>
                    <button className="font-mono font-medium hover:underline text-left"
                      onClick={() => product && setLocation(`/products/${product.id}`)}>
                      {product?.serialNumber || "—"}
                    </button>
                  </div>
                  <div>
                    <p className="text-muted-foreground text-xs">Under Warranty</p>
                    <p className="font-medium">
                      {job.warrantyAtIntake === "yes" ? "Yes" : "No"}
                      {job.warrantyExpiry && (
                        <span className="text-muted-foreground ml-2 text-xs">(Expires {formatDate(job.warrantyExpiry)})</span>
                      )}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
            </ActiveCard>

            {/* Fault & Service Details card */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                  Fault & Service Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-sm">
                <div>
                  <div className="flex items-center justify-between">
                    <p className="text-muted-foreground text-xs">Fault Reported (Complaint)</p>
                    {!editingComplaint && !isClosed && (
                      <button className="text-xs text-muted-foreground hover:text-foreground"
                        onClick={() => { setComplaintText(job.faultReported || ""); setEditingComplaint(true); }}>
                        <Pencil className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                  {editingComplaint ? (
                    <div className="mt-2 space-y-2">
                      <Textarea value={complaintText} onChange={(e) => setComplaintText(e.target.value)} rows={3}
                        placeholder="Describe the fault reported by the customer…" className="text-sm" autoFocus />
                      <div className="flex gap-2">
                        <Button size="sm" onClick={() => updateJob.mutate({ id: job.id, faultReported: complaintText })}
                          disabled={!complaintText.trim() || updateJob.isPending}>
                          <Save className="h-3.5 w-3.5 mr-1" />Save
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setEditingComplaint(false)}>Cancel</Button>
                      </div>
                    </div>
                  ) : (
                    <p className="mt-1 whitespace-pre-wrap">{job.faultReported}</p>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-4">
                  {/* Billed Repair Cost — shown once invoice has been raised */}
                  {(job as any).roInvoiceAmount && (job as any).roInvoiceStatus && (job as any).roInvoiceStatus !== "pending" && (
                    <div className="col-span-2 pt-2 border-t">
                      <p className="text-muted-foreground text-xs">Repair Cost (Billed)</p>
                      <p className="font-semibold text-base">
                        SGD {Number((job as any).roInvoiceAmount).toFixed(2)}
                        <span className="ml-2 text-xs font-normal px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 capitalize">
                          {(job as any).roInvoiceStatus === "paid" ? "Paid" : (job as any).roInvoiceStatus === "confirmed" ? "Confirmed" : "Raised"}
                        </span>
                      </p>
                      {(job as any).roInvoiceNumber && (
                        <p className="text-xs text-muted-foreground mt-0.5">Invoice {(job as any).roInvoiceNumber}</p>
                      )}
                    </div>
                  )}
                </div>
                {/* Tracking link */}
                {trackingUrl && (
                  <div className="pt-2 border-t">
                    <p className="text-muted-foreground text-xs mb-1">Customer Tracking Link</p>
                    <div className="flex items-center gap-2">
                      <code className="text-xs bg-gray-50 border rounded px-2 py-1 flex-1 truncate">{trackingUrl}</code>
                      <button className="text-xs text-muted-foreground hover:text-foreground shrink-0"
                        onClick={() => { navigator.clipboard.writeText(trackingUrl); toast.success("Link copied"); }}>
                        <Copy className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </RoStageSection>
        </div>

        {/* ════════════════════════════════════════════════════════════════════
            SECTION 2 — SERVICE TEAM
            Covers: diagnosis, awaiting_customer_confirmation, customer_declined,
                    in_progress, pending_parts, repair_complete
        ════════════════════════════════════════════════════════════════════ */}
        <div id="diagnosis" data-testid="ro-stage-diagnosis" className="scroll-mt-24">
          <RoStageSection
            label="Diagnosis"
            active={stageUi("diagnosis").active}
            past={stageUi("diagnosis").past}
            live={liveWork.active && liveWork.stageId === "diagnosis"}
            open={openStageId === "diagnosis"}
            onRequestExpand={() => setOpenStageId("diagnosis")}
          >

            {/* ─── DIAGNOSIS CARD ─── */}
            <ActiveCard cardId="diagnosis" activeCardId={activeCardId} dept="service">
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                    Diagnosis
                  </CardTitle>
                  {!editingDiagnosis && !isClosed && (
                    <button className="text-xs text-muted-foreground hover:text-foreground"
                      onClick={() => {
                        setDiagnosisRawNotes((job as any).diagnosedFaultRaw || "");
                        setDiagnosisText((job as any).diagnosedFault || "");
                        setRecommendedRepair((job as any).recommendedRepair || "");
                        setRepairCostInternal(String((job as any).repairCostInternal || ""));
                        setEstimatedWeeks(String((job as any).estimatedWeeks || ""));
                        setEditingDiagnosis(true);
                      }}>
                      <Pencil className="h-3 w-3" />
                    </button>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-4 text-sm">
                {/* ── Finance: Pending parts cost resolution ── */}
                {job.status === "pending_finance" && (isFinance || isAdmin) && (
                  <div className="p-3 bg-purple-50 border border-purple-300 rounded-md space-y-2">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold uppercase tracking-widest text-purple-700">Finance Action Required</span>
                      <span className="text-[10px] bg-purple-200 text-purple-800 px-1.5 py-0.5 rounded font-semibold">PENDING FINANCE</span>
                    </div>
                    <p className="text-xs text-purple-700">The technician does not know the parts cost. Please enter the internal repair cost below to proceed.</p>
                    <div className="flex gap-2">
                      <Input type="number" min="0" step="0.01"
                        placeholder="Internal parts cost (SGD)"
                        value={financePartsCost}
                        onChange={(e) => setFinancePartsCost(e.target.value)}
                        className="h-8 text-sm bg-white flex-1" />
                      <Button size="sm" className="bg-purple-600 hover:bg-purple-700 text-white shrink-0"
                        disabled={!financePartsCost || resolveFinanceReferral.isPending}
                        onClick={() => resolveFinanceReferral.mutate({ jobId: job.id, repairCostInternal: financePartsCost })}>
                        {resolveFinanceReferral.isPending ? "Saving…" : "Submit Cost"}
                      </Button>
                    </div>
                  </div>
                )}
                {job.status === "pending_finance" && !isFinance && !isAdmin && (
                  <div className="p-3 bg-purple-50 border border-purple-200 rounded-md">
                    <p className="text-xs text-purple-700 font-medium">This RO is awaiting Finance to enter the parts cost before it can proceed to quoting.</p>
                  </div>
                )}
                {editingDiagnosis ? (
                  <div className="space-y-4">
                    {/* Internal Notes — staff only */}
                    <div className="space-y-1 p-3 bg-amber-50 border border-amber-200 rounded-md">
                      <p className="text-[10px] font-bold uppercase tracking-widest text-amber-700">Internal Notes (Staff Only — not shown to customer)</p>
                      <Textarea
                        value={diagnosisRawNotes}
                        onChange={(e) => setDiagnosisRawNotes(e.target.value)}
                        rows={3}
                        placeholder="Your raw technical notes about the fault found…"
                        className="text-sm bg-white"
                      />
                    </div>

                    {/* Customer-facing diagnosis */}
                    <div className="space-y-1">
                      <div className="flex items-center justify-between">
                        <p className="text-xs text-muted-foreground">Customer-Facing Diagnosis</p>
                        <button type="button" onClick={handlePolishDiagnosis}
                          disabled={(!diagnosisRawNotes.trim() && !diagnosisText.trim()) || aiPolishing}
                          className="flex items-center gap-1 text-xs text-purple-600 hover:text-purple-800 disabled:opacity-40">
                          <Sparkles className="h-3 w-3" />
                          {aiPolishing ? "Rewriting…" : "AI Rewrite from Notes"}
                        </button>
                      </div>
                      <Textarea
                        value={diagnosisText}
                        onChange={(e) => setDiagnosisText(e.target.value)}
                        rows={3}
                        placeholder="Customer-facing description of the diagnosed fault…"
                        className="text-sm"
                      />
                    </div>

                    <div className="space-y-1">
                      <p className="text-xs text-muted-foreground">Recommended repair (customer-facing)</p>
                      <Textarea
                        value={recommendedRepair}
                        onChange={(e) => setRecommendedRepair(e.target.value)}
                        rows={2}
                        placeholder="Recommended repair the customer will see…"
                        className="text-sm"
                      />
                    </div>

                    {/* Internal Repair Cost + Estimated Weeks — assigned technician, Admin, Finance */}
                    {(isAdmin || isFinance || isService) && (
                      <div className="space-y-3 p-3 bg-amber-50 border border-amber-200 rounded-md">
                        <div className="grid grid-cols-2 gap-3">
                          {canEnterSuggestedInternalRepairCost(
                            { userId: user?.id, role: user?.role, secondaryRolesJson: (user as { secondaryRolesJson?: unknown } | null)?.secondaryRolesJson },
                            { assignedTechnicianId: job.assignedTechnicianId ?? null },
                          ) ? (
                          <div className="space-y-1">
                            <p className="text-[10px] font-bold uppercase tracking-widest text-amber-700">Internal Repair Cost (S$)</p>
                            <Input type="number" min="0" step="0.01" value={repairCostInternal}
                              onChange={(e) => { setRepairCostInternal(e.target.value); setPartsReferToFinance(false); }}
                              placeholder="0.00" className="h-8 text-sm bg-white"
                              disabled={partsReferToFinance} />
                            <p className="text-[10px] text-amber-600">Suggested internal cost — not shown to the customer</p>
                          </div>
                          ) : null}
                          <div className="space-y-1">
                            <p className="text-[10px] font-bold uppercase tracking-widest text-amber-700">Est. Repair Duration (weeks)</p>
                            <Input type="number" min="1" step="1" value={estimatedWeeks}
                              onChange={(e) => setEstimatedWeeks(e.target.value)} placeholder="e.g. 2" className="h-8 text-sm bg-white" />
                            <p className="text-[10px] text-amber-600">Minimum 1 week</p>
                          </div>
                        </div>
                        <label className="flex items-center gap-2 cursor-pointer select-none">
                          <input type="checkbox" checked={partsReferToFinance}
                            onChange={(e) => { setPartsReferToFinance(e.target.checked); if (e.target.checked) setRepairCostInternal(""); }}
                            className="w-4 h-4 rounded accent-amber-600" />
                          <span className="text-sm font-medium text-amber-800">I don't know the parts cost — refer to Finance</span>
                        </label>
                        {partsReferToFinance && (
                          <p className="text-xs text-amber-700 bg-amber-100 border border-amber-300 rounded px-2 py-1">
                            Finance will be notified to enter the parts cost. The RO will be marked <strong>Pending Finance</strong> until they do.
                          </p>
                        )}
                      </div>
                    )}

                    {/* Save button */}
                    <div className="flex gap-2 pt-1">
                      <Button size="sm" className="bg-amber-600 hover:bg-amber-700 text-white gap-1.5"
                        onClick={() => saveDiagnosis.mutate({
                          jobId: job.id,
                          diagnosedFaultRaw: diagnosisRawNotes || diagnosisText,
                          diagnosedFault: diagnosisText,
                          recommendedRepair: recommendedRepair || undefined,
                          repairCostInternal: partsReferToFinance ? undefined : (repairCostInternal || undefined),
                          estimatedWeeks: estimatedWeeks ? parseInt(estimatedWeeks) : undefined,
                          partsReferToFinance: partsReferToFinance || undefined,
                        })}
                        disabled={!diagnosisText.trim() || saveDiagnosis.isPending}>
                        <Save className="h-3.5 w-3.5" />
                        {saveDiagnosis.isPending ? "Saving…" : "Save Diagnosis"}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setEditingDiagnosis(false)}>Cancel</Button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {/* Internal notes (collapsible) */}
                    {(job as any).diagnosedFaultRaw && (
                      <details className="group">
                        <summary className="text-[10px] text-amber-700 font-bold uppercase tracking-widest cursor-pointer hover:text-amber-900 list-none flex items-center gap-1">
                          <ChevronRight className="h-3 w-3 group-open:rotate-90 transition-transform" />
                          Internal Notes (Staff Only)
                        </summary>
                        <p className="text-sm whitespace-pre-wrap mt-2 text-muted-foreground pl-4 border-l-2 border-amber-200">{(job as any).diagnosedFaultRaw}</p>
                      </details>
                    )}

                    {/* Customer-facing diagnosis */}
                    <div>
                      <p className="text-[10px] text-muted-foreground uppercase tracking-wide mb-1">Customer-Facing Diagnosis</p>
                      {(job as any).diagnosedFault ? (
                        <p className="whitespace-pre-wrap">{(job as any).diagnosedFault}</p>
                      ) : (
                        <p className="text-muted-foreground italic">No diagnosis entered yet</p>
                      )}
                    </div>
                    {(job as any).recommendedRepair && (
                      <div>
                        <p className="text-[10px] text-muted-foreground uppercase tracking-wide mb-1">Recommended Repair</p>
                        <p className="whitespace-pre-wrap">{(job as any).recommendedRepair}</p>
                      </div>
                    )}

                    {/* Staff-only fields */}
                    {(showSuggestedRepairCost || (job as { estimatedWeeks?: number | null }).estimatedWeeks) && (isAdmin || isFinance || isService) && (
                      <div className="flex flex-wrap gap-4 pt-2 border-t">
                        {showSuggestedRepairCost && (job as any).repairCostInternal && (
                          <div>
                            <p className="text-[10px] text-amber-700 font-bold uppercase tracking-widest">Internal Cost</p>
                            <p className="font-medium text-sm">S${(job as any).repairCostInternal}</p>
                          </div>
                        )}
                        {(job as any).estimatedWeeks && (
                          <div>
                            <p className="text-[10px] text-amber-700 font-bold uppercase tracking-widest">Est. Duration</p>
                            <p className="font-medium text-sm">{(job as any).estimatedWeeks} {(job as any).estimatedWeeks === 1 ? "week" : "weeks"}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

              </CardContent>
            </Card>
            </ActiveCard>
          </RoStageSection>
        </div>

        {repairApprovedAt && (isAdmin || isFinance || user?.id === job.salespersonId) && workflow?.notifyOutbox?.some((row) => ["approval_pic", "approval_tech"].includes(row.kind) && ["pending", "failed", "sending"].includes(row.status)) && (
          <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm">
            <p>Repair approval is recorded. Staff notification is incomplete; check technician assignment and retry. A delivery marked SENDING is not resent automatically; ask Admin to reconcile it if it remains stuck.</p>
            <Button size="sm" variant="outline" disabled={retryRepairProposalNotify.isPending} onClick={() => retryRepairProposalNotify.mutate({ jobId: job.id })}>Retry staff notifications</Button>
          </div>
        )}

        <div id="approval" data-testid="ro-stage-approval" className="scroll-mt-24">
          <RoStageSection
            label="Customer Approval"
            active={stageUi("approval").active}
            past={stageUi("approval").past}
            open={openStageId === "approval"}
            onRequestExpand={() => setOpenStageId("approval")}
          >
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Repair proposal</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-sm">
                {/* ─── Admin: Set Customer Charge ─── */}
                {(isAdmin || isFinance) && (job as any).diagnosedFault && !repairApprovedAt && job.status !== "customer_declined" && job.status !== "in_progress" && (
                  <div className="border-t pt-4">
                    <p className="text-[10px] font-bold uppercase tracking-widest text-blue-700 mb-2">Admin/Finance: Confirm customer selling price</p>
                    {customerCharge ? (
                      <div className="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-md">
                        <div>
                          <p className="text-sm font-semibold text-blue-800">Customer Charge: S${customerCharge}</p>
                          <p className="text-xs text-blue-600 mt-0.5">Confirmed selling price. The PIC can record customer approval or send an approval request.</p>
                        </div>
                        {!editingCharge && (
                          <button className="text-xs text-blue-600 hover:text-blue-800" onClick={() => { setChargeAmount(String(customerCharge)); setEditingCharge(true); }}>
                            <Pencil className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                    ) : (
                      <p className="text-xs text-muted-foreground italic mb-2">
                        Technician has entered internal cost. Review and set the amount to charge the customer.
                      </p>
                    )}
                    {editingCharge || !customerCharge ? (
                      <div className="space-y-2 mt-2">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-blue-700">S$</span>
                          <Input type="number" min="0" step="0.01" value={chargeAmount}
                            onChange={(e) => setChargeAmount(e.target.value)}
                            placeholder="Amount to charge customer" className="h-8 text-sm" />
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Confirm or replace the technician suggestion as the customer selling price. This does not approve the repair or email the customer.
                        </p>
                        <div className="flex gap-2 flex-wrap">
                          <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white gap-1.5"
                            disabled={!chargeAmount || adminSetCharge.isPending}
                            onClick={() => adminSetCharge.mutate({ jobId: job.id, customerCharge: parseFloat(chargeAmount), origin: window.location.origin })}>
                            <CheckCircle2 className="h-3.5 w-3.5" />
                            {adminSetCharge.isPending ? "Saving…" : "Confirm selling price"}
                          </Button>
                          <Button size="sm" variant="outline" className="border-red-300 text-red-600 hover:bg-red-50 gap-1.5"
                            disabled={adminSetNoCharge.isPending}
                            onClick={() => setShowNoChargeInput(!showNoChargeInput)}>
                            <XCircle className="h-3.5 w-3.5" />
                            Non-billable / Warranty
                          </Button>
                          {editingCharge && (
                            <Button size="sm" variant="outline" onClick={() => setEditingCharge(false)}>Cancel</Button>
                          )}
                        </div>
                        {showNoChargeInput && (
                          <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-md space-y-2">
                            <p className="text-xs font-semibold text-red-700">Mark as Non-billable</p>
                            <p className="text-xs text-red-600">This records the warranty/no-charge decision without changing the repair status. The RO will remain open.</p>
                            <Input
                              placeholder="Reason (e.g. warranty, goodwill, internal)"
                              value={noChargeReason}
                              onChange={(e) => setNoChargeReason(e.target.value)}
                              className="h-8 text-sm"
                            />
                            <div className="flex gap-2">
                              <Button size="sm" className="bg-red-600 hover:bg-red-700 text-white gap-1.5"
                                disabled={adminSetNoCharge.isPending}
                                onClick={() => adminSetNoCharge.mutate({ jobId: job.id, reason: noChargeReason || undefined, action: "classify_non_billable" })}>
                                <CheckCircle2 className="h-3.5 w-3.5" />
                                {adminSetNoCharge.isPending ? "Saving…" : "Save as Non-billable"}
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => setShowNoChargeInput(false)}>Cancel</Button>
                            </div>
                          </div>
                        )}
                      </div>
                    ) : null}
                  </div>
                )}



                {job.status === "ready_to_send_to_customer" && (
                  <div className="border-t pt-4">
                    <div className="p-3 bg-sky-50 border border-sky-200 rounded-lg space-y-2">
                      <p className="text-sm font-semibold text-sky-900">Proposal ready — PIC decision</p>
                      <p className="text-xs text-sky-800">Review the confirmed proposal, then approve on behalf of the customer or send the customer an approval request.</p>
                      {!proposalCustomerSend.canSend && (
                        <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900">
                          <p className="font-semibold">Customer email required</p>
                          <p className="mt-1">{proposalCustomerSend.explanation}</p>
                          <Button asChild size="sm" variant="outline" className="mt-2 bg-white">
                            <Link href={proposalCustomerSend.customerHref}>Open customer record</Link>
                          </Button>
                        </div>
                      )}
                      {(workflow as { proposalPreview?: { customerFacingDiagnosis?: string | null; proposedRepair?: string | null; sellingPrice?: string | null; estimatedDuration?: string | null } } | undefined)?.proposalPreview && (
                        <div className="text-xs space-y-1 bg-white/70 border border-sky-100 rounded p-2">
                          <p><span className="font-semibold">Diagnosis:</span> {(workflow as any).proposalPreview.customerFacingDiagnosis || "—"}</p>
                          <p><span className="font-semibold">Proposed repair:</span> {(workflow as any).proposalPreview.proposedRepair || "—"}</p>
                          {(isAdmin || isFinance || isSales || user?.id === job.salespersonId) && (
                            <p><span className="font-semibold">Selling price:</span> S${(workflow as any).proposalPreview.sellingPrice || "—"}</p>
                          )}
                          <p><span className="font-semibold">Estimated duration:</span> {(workflow as any).proposalPreview.estimatedDuration || "—"}</p>
                        </div>
                      )}
                      {user?.id === job.salespersonId && (
                        <Button
                          size="sm"
                          className="w-full bg-sky-700 hover:bg-sky-800 text-white"
                          disabled={sendRepairProposal.isPending || approveOnBehalf.isPending || !proposalCustomerSend.canSend}
                          onClick={() => sendRepairProposal.mutate({ jobId: job.id, origin: window.location.origin })}
                        >
                          {sendRepairProposal.isPending ? "Sending…" : "SEND PROPOSAL TO CLIENT"}
                        </Button>
                      )}
                      {user?.id !== job.salespersonId && (
                        <p className="text-xs text-sky-800">Waiting for the assigned PIC to record approval or send the proposal.</p>
                      )}
                      {(isAdmin || isFinance || isSales || user?.id === job.salespersonId) && Array.isArray((workflow as any)?.notifyOutbox) && (workflow as any).notifyOutbox.length > 0 && (
                        <div className="text-[11px] space-y-1">
                          {((workflow as any).notifyOutbox as { kind: string; status: string; lastError?: string | null }[]).map((row, idx) => (
                            <p key={`${row.kind}-${idx}`}>
                              Notification {row.kind}: <span className="uppercase font-semibold">{row.status}</span>
                              {row.lastError ? ` — ${row.lastError}` : ""}
                            </p>
                          ))}
                        </div>
                      )}
                      {(isAdmin || isFinance || isSales) && (
                        <Button size="sm" variant="outline" onClick={() => retryRepairProposalNotify.mutate({ jobId: job.id })}>
                          Retry failed notifications
                        </Button>
                      )}
                    </div>
                  </div>
                )}

                {job.status === "awaiting_customer_confirmation" && (
                  <div className="border-t pt-4">
                    <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg space-y-2">
                      <div className="flex items-center gap-2">
                        <AlertTriangle className="h-4 w-4 text-orange-600 shrink-0" />
                        <p className="text-sm font-semibold text-orange-800">Awaiting Customer Approval</p>
                      </div>
                      {customerCharge && (isAdmin || isFinance || isSales) && (
                        <p className="text-xs text-orange-700">Quoted amount: <span className="font-semibold">S${customerCharge}</span></p>
                      )}
                      {(job as any).quoteSentAt ? (
                        <p className="text-xs text-orange-700">Proposal emailed on {formatDateTime((job as any).quoteSentAt)}</p>
                      ) : (
                        <p className="text-xs text-orange-700">Proposal email has not been accepted by the provider yet. Use Retry.</p>
                      )}
                      {user?.id === job.salespersonId && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => sendRepairProposal.mutate({ jobId: job.id, origin: window.location.origin })}
                        >
                          Retry send
                        </Button>
                      )}
                    </div>
                  </div>
                )}

                {/* Customer declined */}
                {job.status === "customer_declined" && (
                  <div className="border-t pt-4">
                    <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                      <XCircle className="h-4 w-4 text-red-600 shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-semibold text-red-800">Customer Declined Repair</p>
                        {(job as any).customerDeclinedAt && (
                          <p className="text-xs text-red-700 mt-0.5">Declined on {formatDateTime((job as any).customerDeclinedAt)}</p>
                        )}
                        <p className="text-xs text-red-700 mt-1">PIC should follow up with the customer to discuss next steps.</p>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </RoStageSection>
        </div>

        <div id="repair" data-testid="ro-stage-repair" className="scroll-mt-24">
          <RoStageSection
            label="Repair"
            active={stageUi("repair").active}
            past={stageUi("repair").past}
            live={liveWork.active && liveWork.stageId === "repair"}
            open={openStageId === "repair"}
            onRequestExpand={() => setOpenStageId("repair")}
          >
            {repairApprovedAt && (
              <ActiveCard cardId="approved" activeCardId={activeCardId} dept="service">
                <div className="relative flex items-center gap-3 px-4 py-3 bg-green-600 text-white rounded-lg shadow-sm">
                  <CheckCircle2 className="h-5 w-5 shrink-0" />
                  <div className="flex-1">
                    <p className="font-bold text-sm tracking-wide">REPAIR APPROVED — PLEASE PROCEED</p>
                    <p className="text-xs text-green-100 mt-0.5">
                      {formatDateTime(repairApprovedAt)}
                      {repairApprovedBy && ` — ${repairApprovedBy}`}
                    </p>
                  </div>
                </div>
              </ActiveCard>
            )}

            {/* Repair Performed card */}
            <ActiveCard cardId="repair" activeCardId={activeCardId} dept="service">
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                    Repair Performed
                  </CardTitle>
                  {!addingReportEntry && !isClosed && (
                    <Button size="sm" className="bg-black text-white hover:bg-gray-800 h-7 text-xs"
                      onClick={() => setAddingReportEntry(true)}>
                      + Add Entry
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent className="text-sm space-y-4">
                {addingReportEntry && (
                  <div className="border border-amber-200 bg-amber-50/30 rounded-md p-4 space-y-3">
                    <p className="text-xs font-semibold text-amber-800 uppercase tracking-wide">New Service Entry</p>
                    <div>
                      <label className="text-xs text-muted-foreground block mb-1">Date of Work</label>
                      <input type="date" value={newEntryDate} onChange={(e) => setNewEntryDate(e.target.value)}
                        className="w-full border rounded-md px-3 py-1.5 text-sm" />
                    </div>
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <label className="text-xs text-muted-foreground">Internal Notes (not shown to customer)</label>
                      </div>
                      <Textarea value={newEntryRawNotes} onChange={(e) => setNewEntryRawNotes(e.target.value)} rows={3}
                        placeholder="Your raw notes about the work done…" className="text-sm" />
                    </div>
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <label className="text-xs text-muted-foreground">Customer-Facing Report</label>
                        <button type="button" onClick={handlePolishEntryNotes}
                          disabled={!newEntryRawNotes.trim() || aiPolishingEntry}
                          className="flex items-center gap-1 text-xs text-purple-600 hover:text-purple-800 disabled:opacity-40">
                          <Sparkles className="h-3 w-3" />
                          {aiPolishingEntry ? "Rewriting…" : "AI Rewrite from Notes"}
                        </button>
                      </div>
                      <Textarea value={newEntryCustomerReport} onChange={(e) => setNewEntryCustomerReport(e.target.value)} rows={3}
                        placeholder="Customer-facing summary of work done…" className="text-sm" />
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" className="bg-black text-white hover:bg-gray-800"
                        onClick={() => addReportEntry.mutate({ jobId: job.id, entryDate: new Date(newEntryDate), rawNotes: newEntryRawNotes || undefined, customerReport: newEntryCustomerReport || undefined })}
                        disabled={addReportEntry.isPending || (!newEntryRawNotes.trim() && !newEntryCustomerReport.trim())}>
                        <Save className="h-3.5 w-3.5 mr-1" />
                        {addReportEntry.isPending ? "Saving…" : "Save Entry"}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => { setAddingReportEntry(false); setNewEntryRawNotes(""); setNewEntryCustomerReport(""); }}>Cancel</Button>
                    </div>
                  </div>
                )}
                {/* Legacy single serviceReport */}
                {reportEntries.length === 0 && (job as any).serviceReport && (
                  <div className="border rounded-md p-3 bg-gray-50">
                    <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Imported Record</span>
                    <p className="whitespace-pre-wrap text-sm mt-2">{(job as any).serviceReport}</p>
                  </div>
                )}
                {reportEntries.length === 0 && !(job as any).serviceReport && !addingReportEntry && (
                  <p className="text-muted-foreground italic text-sm py-2">
                    No repair entries yet.{!isClosed && " Click \"+ Add Entry\" to record work done."}
                  </p>
                )}
                {reportEntries.map((entry, idx) => (
                  <div key={entry.id} className="border rounded-md overflow-hidden">
                    <div className="flex items-center justify-between px-3 py-2 bg-gray-50 border-b">
                      <div className="flex items-center gap-3">
                        <span className="text-xs font-semibold">{formatDate(entry.entryDate)}</span>
                        <span className="text-xs text-muted-foreground">{entry.technicianName}</span>
                        {idx === 0 && <span className="px-1.5 py-0.5 bg-green-100 text-green-700 text-[10px] rounded font-medium">Latest</span>}
                      </div>
                      {isAdmin && !isClosed && (
                        <button onClick={() => { if (confirm("Delete this service entry?")) deleteReportEntry.mutate({ id: entry.id }); }}
                          className="text-xs text-red-500 hover:text-red-700">Delete</button>
                      )}
                    </div>
                    <div className="p-3 space-y-2">
                      {entry.customerReport && (
                        <div>
                          <p className="text-[10px] text-muted-foreground uppercase tracking-wide mb-1">Customer Report</p>
                          <p className="text-sm whitespace-pre-wrap">{entry.customerReport}</p>
                        </div>
                      )}
                      {entry.rawNotes && (
                        <details className="group">
                          <summary className="text-[10px] text-muted-foreground uppercase tracking-wide cursor-pointer hover:text-foreground list-none flex items-center gap-1">
                            <ChevronRight className="h-3 w-3 group-open:rotate-90 transition-transform" />
                            Internal Notes
                          </summary>
                          <p className="text-sm whitespace-pre-wrap mt-1 text-muted-foreground pl-4">{entry.rawNotes}</p>
                        </details>
                      )}
                    </div>
                  </div>
                ))}
              </CardContent>
              {/* Send to Finance button — shown when repair is complete and not yet sent to finance */}
              {(isAdmin || isService) && job.status === "repair_complete" && (
                <div className="px-6 pb-4">
                  <div className={`rounded-lg p-4 border-2 ${
                    repairJustSaved
                      ? "border-green-500 bg-green-50 animate-pulse-once"
                      : "border-blue-200 bg-blue-50"
                  }`}>
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white text-sm font-bold">
                        1
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-semibold text-gray-900">
                          {repairJustSaved ? "✅ Repair saved — next step: send to Finance" : "Next step: Send to Finance"}
                        </p>
                        <p className="text-xs text-muted-foreground mt-0.5 mb-3">
                          Finance will generate the invoice and send it to the customer for payment.
                        </p>
                        <Button
                          size="sm"
                          className="bg-blue-600 text-white hover:bg-blue-700 gap-1.5 shadow-sm"
                          onClick={() => { updateStatus.mutate({ id: job.id, status: "awaiting_payment" }); setRepairJustSaved(false); }}
                          disabled={updateStatus.isPending}
                        >
                          {updateStatus.isPending ? "Sending…" : "Send to Finance"}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </Card>
            </ActiveCard>
          </RoStageSection>
        </div>

        {/* ════════════════════════════════════════════════════════════════════
            SECTION 3 — FINANCE
            Covers: awaiting_payment
        ════════════════════════════════════════════════════════════════════ */}
        <div id="finance" data-testid="ro-stage-finance" className="scroll-mt-24">
          <RoStageSection
            label="Finance"
            active={stageUi("finance").active}
            past={stageUi("finance").past}
            open={openStageId === "finance"}
            onRequestExpand={() => setOpenStageId("finance")}
          >

            <ActiveCard cardId="invoice" activeCardId={activeCardId} dept="finance">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-2">
                  <FileText className="h-4 w-4" />Invoice
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* ── Step 1: Generate ─────────────────────────────────── */}
                {(isAdmin || isFinance) && (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <span className={`w-5 h-5 rounded-full text-xs font-bold flex items-center justify-center shrink-0 ${
                        (job as any).roInvoiceStatus && (job as any).roInvoiceStatus !== "pending"
                          ? "bg-blue-500 text-white" : "bg-gray-200 text-gray-600"
                      }`}>1</span>
                      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Generate Invoice</p>
                      {(job as any).roInvoiceStatus && (job as any).roInvoiceStatus !== "pending" && (
                        <CheckCircle2 className="h-3.5 w-3.5 text-blue-500 ml-auto" />
                      )}
                    </div>
                    {(job as any).roInvoiceNumber ? (
                      <div className="space-y-2">
                        <div className="flex items-center justify-between p-3 bg-blue-50 border border-blue-100 rounded-md">
                          <div className="text-sm">
                            <p className="font-medium">{(job as any).roInvoiceNumber}</p>
                            <p className="text-xs text-muted-foreground">SGD {(job as any).roInvoiceAmount ? Number((job as any).roInvoiceAmount).toFixed(2) : "—"} · Generated {formatDate((job as any).roInvoiceRaisedAt ?? job.invoiceUploadedAt)}</p>
                            {(job as any).roPaymentLink && (
                              <a href={(job as any).roPaymentLink} target="_blank" rel="noopener noreferrer"
                                className="text-xs text-blue-600 hover:underline">Payment link ↗</a>
                            )}
                          </div>
                          <a href={job.invoiceUrl ?? ""} target="_blank" rel="noopener noreferrer"
                            className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800">
                            <Download className="h-3.5 w-3.5" />PDF
                          </a>
                        </div>
                        {/* Regenerate — Finance/Admin can change amount or description */}
                        <div className="space-y-1.5">
                          <p className="text-[10px] text-muted-foreground uppercase tracking-wide font-medium">Regenerate (change amount or description)</p>
                          <Input
                            type="number" min="0" step="0.01"
                            placeholder="New amount (SGD incl. GST)"
                            defaultValue={(job as any).roInvoiceAmount ? Number((job as any).roInvoiceAmount).toFixed(2) : ""}
                            value={roInvoiceAmount}
                            onChange={(e) => setRoInvoiceAmount(e.target.value)}
                            className="h-8 text-sm"
                          />
                          <Input
                            placeholder="New description (leave blank to keep current)"
                            value={roInvoiceDescription}
                            onChange={(e) => setRoInvoiceDescription(e.target.value)}
                            className="h-8 text-sm"
                          />
                          <Button size="sm" variant="outline" className="w-full"
                            disabled={!roInvoiceAmount || generateROInvoice.isPending}
                            onClick={() => generateROInvoice.mutate({
                              jobId: job.id,
                              amount: parseFloat(roInvoiceAmount),
                              description: roInvoiceDescription || undefined,
                            })}>
                            {generateROInvoice.isPending ? "Regenerating…" : "Regenerate Invoice & Payment Link"}
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <div className="flex gap-2">
                          <Input
                            type="number" min="0" step="0.01"
                            placeholder={`Amount incl. GST (SGD)${job.customerCharge ? ` — suggested: ${job.customerCharge}` : ""}`}
                            value={roInvoiceAmount}
                            onChange={(e) => setRoInvoiceAmount(e.target.value)}
                            className="h-8 text-sm"
                          />
                        </div>
                        <Input
                          placeholder="Description (optional — auto-filled from report)"
                          value={roInvoiceDescription}
                          onChange={(e) => setRoInvoiceDescription(e.target.value)}
                          className="h-8 text-sm"
                        />
                        <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white w-full"
                          disabled={!roInvoiceAmount || generateROInvoice.isPending}
                          onClick={() => generateROInvoice.mutate({
                            jobId: job.id,
                            amount: parseFloat(roInvoiceAmount),
                            description: roInvoiceDescription || undefined,
                          })}>
                          {generateROInvoice.isPending ? "Generating…" : "Generate Invoice & Payment Link"}
                        </Button>
                      </div>
                    )}
                  </div>
                )}

                {/* ── Step 1b: Invoice summary visible to Sales (read-only) ─────── */}
                {isSales && !isAdmin && !isFinance && (job as any).roInvoiceNumber && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between p-3 bg-blue-50 border border-blue-100 rounded-md">
                      <div className="text-sm">
                        <p className="font-medium">{(job as any).roInvoiceNumber}</p>
                        <p className="text-xs text-muted-foreground">SGD {(job as any).roInvoiceAmount ? Number((job as any).roInvoiceAmount).toFixed(2) : "—"} · Generated {formatDate((job as any).roInvoiceRaisedAt ?? job.invoiceUploadedAt)}</p>
                        {(job as any).roPaymentLink && (
                          <a href={(job as any).roPaymentLink} target="_blank" rel="noopener noreferrer"
                            className="text-xs text-blue-600 hover:underline">Payment link ↗</a>
                        )}
                      </div>
                      {job.invoiceUrl && (
                        <a href={job.invoiceUrl} target="_blank" rel="noopener noreferrer"
                          className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800">
                          <Download className="h-3.5 w-3.5" />PDF
                        </a>
                      )}
                    </div>
                  </div>
                )}

                {/* ── Step 2: Confirm ──────────────────────────────────── */}
                {(isAdmin || isFinance || isSales) && (job as any).roInvoiceNumber && (
                  <div className="space-y-2 pt-2 border-t">
                    <div className="flex items-center gap-2">
                      <span className={`w-5 h-5 rounded-full text-xs font-bold flex items-center justify-center shrink-0 ${
                        (job as any).roInvoiceStatus === "confirmed" || (job as any).roInvoiceStatus === "paid"
                          ? "bg-blue-500 text-white" : "bg-gray-200 text-gray-600"
                      }`}>2</span>
                      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Confirm Invoice</p>
                      {((job as any).roInvoiceStatus === "confirmed" || (job as any).roInvoiceStatus === "paid") && (
                        <CheckCircle2 className="h-3.5 w-3.5 text-blue-500 ml-auto" />
                      )}
                    </div>
                    {(job as any).roInvoiceStatus === "raised" && (isAdmin || isFinance) ? (
                      <Button size="sm" variant="outline" className="w-full"
                        disabled={confirmROInvoice.isPending}
                        onClick={() => confirmROInvoice.mutate({ jobId: job.id })}>
                        {confirmROInvoice.isPending ? "Confirming…" : "Confirm Invoice"}
                      </Button>
                    ) : (
                      <p className="text-xs text-muted-foreground">
                        {(job as any).roInvoiceStatus === "confirmed" || (job as any).roInvoiceStatus === "paid"
                          ? `Confirmed by ${(job as any).roInvoiceConfirmedBy ?? "Finance"} on ${formatDate((job as any).roInvoiceConfirmedAt)}`
                          : "Awaiting Finance confirmation"}
                      </p>
                    )}
                  </div>
                )}

                {/* ── Step 3: Send to Customer ─────────────────────────── */}
                {(isAdmin || isFinance || isSales) && ((job as any).roInvoiceStatus === "confirmed" || (job as any).roInvoiceStatus === "paid") && (
                  <div className="space-y-2 pt-2 border-t">
                    <div className="flex items-center gap-2">
                      <span className={`w-5 h-5 rounded-full text-xs font-bold flex items-center justify-center shrink-0 ${
                        (job as any).roInvoiceSentAt ? "bg-blue-500 text-white" : "bg-gray-200 text-gray-600"
                      }`}>3</span>
                      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Send to Customer</p>
                      {(job as any).roInvoiceSentAt && (
                        <CheckCircle2 className="h-3.5 w-3.5 text-blue-500 ml-auto" />
                      )}
                    </div>
                    {(job as any).roInvoiceSentAt ? (
                      <p className="text-xs text-green-700 flex items-center gap-1">
                        <span>✓</span>
                        Successfully sent to customer via {(job as any).roInvoiceSentChannel === "whatsapp" ? "WhatsApp" : (job as any).roInvoiceSentChannel === "email" ? "Email" : (job as any).roInvoiceSentChannel ?? "unknown"} on{" "}
                        {new Date((job as any).roInvoiceSentAt).toLocaleDateString("en-SG", { day: "numeric", month: "long", year: "numeric" })}{" "}
                        {new Date((job as any).roInvoiceSentAt).toLocaleTimeString("en-SG", { hour: "2-digit", minute: "2-digit", hour12: true })}{" "}
                        by {(job as any).roInvoiceSentBy || "—"}
                      </p>
                    ) : null}
                    <div className="flex gap-2">
                      <select
                        className="h-8 text-xs border rounded px-2 bg-background"
                        value={roSendChannel}
                        onChange={(e) => setRoSendChannel(e.target.value as "email" | "whatsapp")}>
                        <option value="email">Email</option>
                        <option value="whatsapp">WhatsApp</option>
                      </select>
                      <Button size="sm" className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                        disabled={sendROInvoice.isPending}
                        onClick={() => sendROInvoice.mutate({ jobId: job.id, channel: roSendChannel })}>
                        <Send className="h-3 w-3 mr-1" />
                        {sendROInvoice.isPending ? "Sending…" : (job as any).roInvoiceSentAt ? "Resend" : "Send Invoice"}
                      </Button>
                    </div>

                    {/* ── Download PDF + Copy Portal Link (visible to all who can send) ── */}
                    <div className="flex gap-2 pt-1">
                      {job.invoiceUrl && (
                        <a href={job.invoiceUrl} target="_blank" rel="noopener noreferrer"
                          className="flex-1 flex items-center justify-center gap-1.5 h-8 text-xs border rounded px-3 bg-background hover:bg-muted transition-colors">
                          <Download className="h-3 w-3" />
                          Download PDF
                        </a>
                      )}
                      {(job as any).roInvoiceToken && (
                        <button
                          className="flex-1 flex items-center justify-center gap-1.5 h-8 text-xs border rounded px-3 bg-background hover:bg-muted transition-colors"
                          onClick={() => {
                            const link = `${window.location.origin}/ro-invoice/${(job as any).roInvoiceToken}`;
                            navigator.clipboard.writeText(link);
                            toast.success("Portal link copied to clipboard");
                          }}>
                          <Copy className="h-3 w-3" />
                          Copy Portal Link
                        </button>
                      )}
                    </div>
                  </div>
                )}

                {/* ── Mark Paid ────────────────────────────────────────── */}
                {(isAdmin || isFinance) && (job as any).roInvoiceStatus === "confirmed" && (
                  <div className="pt-2 border-t">
                    <Button size="sm" variant="outline" className="w-full text-green-700 border-green-300 hover:bg-green-50"
                      disabled={markROPaid.isPending}
                      onClick={() => markROPaid.mutate({ jobId: job.id })}>
                      <CheckCircle2 className="h-3.5 w-3.5 mr-1" />
                      {markROPaid.isPending ? "Marking…" : "Mark as Paid"}
                    </Button>
                  </div>
                )}

                {/* ── Paid confirmation ────────────────────────────────── */}
                {(job as any).roInvoiceStatus === "paid" && (
                  <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-md">
                    <CheckCircle2 className="h-4 w-4 text-green-600 shrink-0" />
                    <p className="text-sm font-medium text-green-800">Payment received</p>
                  </div>
                )}

                {/* ── Customer-facing quote (admin/finance) ────────────── */}
                {(isAdmin || isFinance) && (
                  <div className="pt-2 border-t">
                    <div className="flex items-center justify-between">
                      <p className="text-muted-foreground text-xs">Customer-Facing Quote</p>
                      {!editingCost && !isClosed && (
                        <button className="text-xs text-muted-foreground hover:text-foreground"
                          onClick={() => { setActingCost(String(job.actualCost || "")); setEditingCost(true); }}>
                          <Pencil className="h-3 w-3" />
                        </button>
                      )}
                    </div>
                    {editingCost ? (
                      <div className="flex gap-2 mt-1">
                        <Input type="number" min="0" step="0.01" value={actingCost}
                          onChange={(e) => setActingCost(e.target.value)} placeholder="0.00" className="h-8 text-sm" />
                        <Button size="sm" onClick={() => adminSetCharge.mutate({ jobId: job.id, customerCharge: parseFloat(actingCost), origin: window.location.origin })} disabled={adminSetCharge.isPending || !actingCost}>Save as selling price</Button>
                        <Button size="sm" variant="outline" onClick={() => setEditingCost(false)}>Cancel</Button>
                      </div>
                    ) : (
                      <p className="font-medium mt-1">{job.actualCost ? `S$${job.actualCost}` : "—"}</p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
            </ActiveCard>
          </RoStageSection>
         </div>

        {/* ── BLOCKER BANNER: Finance → PIC/Delivery ── */}
        {job.status === "awaiting_payment" && (
          <div className="space-y-2">
            <div className="flex items-center gap-3 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 mx-0">
              <svg className="h-4 w-4 shrink-0 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
              <p className="text-xs font-bold uppercase tracking-wide text-amber-800 flex-1">Waiting for Payment</p>
              {(isAdmin || isFinance) && (
                <button
                  className="text-xs text-green-700 hover:text-green-900 font-semibold flex items-center gap-1 shrink-0"
                  onClick={() => { setShowNoChargeInput(v => !v); setNoChargeReason(""); }}
                >
                  <CheckCircle2 className="h-3.5 w-3.5" />
                  No Bill / Waive
                </button>
              )}
            </div>
            {showNoChargeInput && (isAdmin || isFinance) && (
              <div className="rounded-md border border-green-200 bg-green-50 p-3 space-y-2">
                <p className="text-xs font-semibold text-green-800">Waive charge — mark as No Bill and close this RO immediately.</p>
                <Input
                  placeholder="Reason (e.g. goodwill, warranty, waived by management)"
                  value={noChargeReason}
                  onChange={(e) => setNoChargeReason(e.target.value)}
                  className="h-8 text-sm"
                />
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    className="bg-green-700 hover:bg-green-800 text-white gap-1.5"
                    disabled={adminSetNoCharge.isPending}
                    onClick={() => adminSetNoCharge.mutate({ jobId: job.id, reason: noChargeReason || undefined, action: "waive_and_close" })}
                  >
                    <CheckCircle2 className="h-3.5 w-3.5" />
                    {adminSetNoCharge.isPending ? "Closing…" : "Confirm No Bill & Close RO"}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => { setShowNoChargeInput(false); setNoChargeReason(""); }}>Cancel</Button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ════════════════════════════════════════════════════════════════════
            SECTION 4 — PIC / DELIVERY
            Covers: awaiting_collection, closed
        ════════════════════════════════════════════════════════════════════ */}
        <div id="collection" data-testid="ro-stage-collection" className="scroll-mt-24">
          <RoStageSection
            label="Collection / Close"
            active={stageUi("collection").active}
            past={stageUi("collection").past}
            open={openStageId === "collection"}
            onRequestExpand={() => setOpenStageId("collection")}
          >

            {/* Customer Notices */}
            <CustomerNoticePanel
              sourceType="ro"
              sourceId={parseInt(id)}
              canSend={isAdmin || isSales}
            />

            {/* Delivery status */}
            <ActiveCard cardId="delivery" activeCardId={activeCardId} dept="pic">
            {(job.status === "awaiting_collection" || job.status === "closed") && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                    Delivery / Collection
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-sm space-y-2">
                  {job.status === "awaiting_collection" && (
                    <div className="space-y-3">
                      <div className="flex items-start gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                        <CheckCircle2 className="h-4 w-4 text-green-600 shrink-0 mt-0.5" />
                        <div>
                          <p className="text-sm font-semibold text-green-800">Payment received — arrange {job.collectionPreference === "delivery" ? "delivery" : "collection"}</p>
                          <p className="text-xs text-green-700 mt-0.5">
                            {job.collectionPreference === "delivery"
                              ? "Coordinate delivery with the customer. Once delivered, click \"Item Collected\" to close the job."
                              : "Notify the customer that the unit is ready for collection. Once collected, click \"Item Collected\" to close the job."}
                          </p>
                        </div>
                      </div>
                      <Button
                        className="w-full bg-green-700 hover:bg-green-800 text-white font-semibold text-sm gap-2"
                        disabled={markCollected.isPending}
                        onClick={() => {
                          if (confirm("Confirm item has been collected / delivered? This will close the RO and notify the customer.")) {
                            markCollected.mutate({ jobId: job.id });
                          }
                        }}
                      >
                        <CheckCircle2 className="h-4 w-4" />
                        {markCollected.isPending ? "Closing RO…" : "✓ Item Collected — Close RO"}
                      </Button>
                    </div>
                  )}
                  {job.status === "closed" && (
                    <div className="space-y-3">
                      <div className="flex items-start gap-2 p-3 bg-gray-50 border rounded-lg">
                        <CheckCircle2 className="h-4 w-4 text-gray-500 shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <p className="text-sm font-semibold text-gray-700">Job closed</p>
                          {job.closedAt && <p className="text-xs text-muted-foreground mt-0.5">Closed on {formatDateTime(job.closedAt)}</p>}
                        </div>
                        {(isAdmin || isFinance) && (
                          <button
                            className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1 shrink-0"
                            onClick={() => { setShowUpdateBilling(!showUpdateBilling); setUpdateBillingChoice(null); setUpdateBillingReason(""); }}
                          >
                            <RefreshCw className="h-3.5 w-3.5" />
                            Update
                          </button>
                        )}
                      </div>

                      {/* Update billing decision panel — only when closed */}
                      {showUpdateBilling && (isAdmin || isFinance) && (
                        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg space-y-3">
                          <p className="text-xs font-bold uppercase tracking-widest text-blue-700">Update Billing Decision</p>
                          <p className="text-xs text-blue-600">This case is closed. Select a new billing decision:</p>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant={updateBillingChoice === "bill" ? "default" : "outline"}
                              className={updateBillingChoice === "bill" ? "bg-blue-600 text-white" : "border-blue-300 text-blue-700"}
                              onClick={() => setUpdateBillingChoice("bill")}
                            >
                              Bill Customer
                            </Button>
                            <Button
                              size="sm"
                              variant={updateBillingChoice === "no_bill" ? "default" : "outline"}
                              className={updateBillingChoice === "no_bill" ? "bg-green-600 text-white" : "border-green-300 text-green-700"}
                              onClick={() => setUpdateBillingChoice("no_bill")}
                            >
                              No Bill
                            </Button>
                          </div>

                          {updateBillingChoice === "bill" && (
                            <div className="space-y-2">
                              <p className="text-xs text-blue-700">Enter the amount to charge the customer. This will re-open the case to awaiting payment.</p>
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-medium text-blue-700">S$</span>
                                <Input
                                  type="number" min="0" step="0.01"
                                  value={chargeAmount}
                                  onChange={(e) => setChargeAmount(e.target.value)}
                                  placeholder="Amount to charge customer"
                                  className="h-8 text-sm"
                                />
                              </div>
                              <div className="flex gap-2">
                                <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white gap-1.5"
                                  disabled={!chargeAmount || adminSetCharge.isPending}
                                  onClick={() => adminSetCharge.mutate({ jobId: job.id, customerCharge: parseFloat(chargeAmount), origin: window.location.origin })}>
                                  <CheckCircle2 className="h-3.5 w-3.5" />
                                  {adminSetCharge.isPending ? "Processing…" : "Set Charge & Re-open"}
                                </Button>
                                <Button size="sm" variant="outline" onClick={() => { setShowUpdateBilling(false); setUpdateBillingChoice(null); }}>Cancel</Button>
                              </div>
                            </div>
                          )}

                          {updateBillingChoice === "no_bill" && (
                            <div className="space-y-2">
                              <p className="text-xs text-green-700">No charge will be applied. Optionally enter a reason.</p>
                              <Input
                                placeholder="Reason (e.g. warranty, goodwill, internal)"
                                value={updateBillingReason}
                                onChange={(e) => setUpdateBillingReason(e.target.value)}
                                className="h-8 text-sm"
                              />
                              <div className="flex gap-2">
                                <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white gap-1.5"
                                  disabled={adminSetNoCharge.isPending}
                                  onClick={() => adminSetNoCharge.mutate({ jobId: job.id, reason: updateBillingReason || undefined, action: "waive_and_close" })}>
                                  <CheckCircle2 className="h-3.5 w-3.5" />
                                  {adminSetNoCharge.isPending ? "Closing…" : "Close This Case"}
                                </Button>
                                <Button size="sm" variant="outline" onClick={() => { setShowUpdateBilling(false); setUpdateBillingChoice(null); }}>Cancel</Button>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
            </ActiveCard>
          </RoStageSection>
        </div>

        <details className="rounded-2xl border border-slate-200 bg-white p-4 text-sm">
          <summary className="cursor-pointer font-semibold text-slate-800">Clarity Repair Order workflow</summary>
          <ol className="mt-3 space-y-3 text-slate-700">
            {CLARITY_REPAIR_ORDER_WORKFLOW_STEPS.map((step) => (
              <li key={step.title}>
                <p className="font-medium text-slate-900">{step.title}</p>
                <p className="mt-0.5 text-xs leading-relaxed text-slate-600">{step.body}</p>
              </li>
            ))}
          </ol>
          <p className="mt-3 text-xs text-slate-500">
            Technicians must not begin the repair before customer approval. Admin/Finance must confirm every customer selling price.
            Only the PIC sends the proposal. Failed notifications must be retried — never ignored.
          </p>
        </details>

        {/* ── Photos ── */}
        {photos && photos.length > 0 && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Photos</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                {photos.map((photo) => (
                  <a key={photo.id} href={photo.url} target="_blank" rel="noopener noreferrer">
                    <img src={photo.url} alt="Job photo" className="w-full h-24 object-cover rounded-md border hover:opacity-90 transition-opacity" />
                  </a>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        </div>
      </div>
      </div>
    </DashboardLayout>
  );
}
